'use client';

import BranchList from "../branch-list";
import { useState, useEffect } from "react";
import ServiceList from "../service-list";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

type BranchInfo = { id: string; name: string; subtitle: string };

interface BottomSheetProps {
  height: number;
  onDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
  onSnapRequest?: (position: 'top' | 'middle' | 'bottom') => void;
  /** When returning from schedule, preselect this branch so the service list is shown */
  initialBranch?: BranchInfo | null;
}

export default function BottomSheet({ height, onDragStart, onSnapRequest, initialBranch = null }: BottomSheetProps) {
  const [selectedBranch, setSelectedBranch] = useState<BranchInfo | null>(initialBranch ?? null);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialBranch) {
      setSelectedBranch(initialBranch);
      onSnapRequest?.('top');
    }
  }, [initialBranch?.id]);

  const handleBranchClick = (branch: {id: string; name: string; subtitle: string }) => {
    setSelectedBranch(branch);
    onSnapRequest?.('top');
  };

  const handleBackToBranches = () => {
    setSelectedBranch(null);
    onSnapRequest?.('middle');
  };

  return (
    <div
      className="w-full bg-white rounded-t-[2rem] shadow-[0_-8px_30px_rgba(0,0,0,0.12)] border-t border-gray-100 flex flex-col  relative z-50 h-full pb-[env(safe-area-inset-bottom)]"
    >
      {/* Drag Handle Area */}
      <div
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        className="w-full flex items-center justify-center pt-4 pb-2 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
      >
        <div className="w-12 h-1.5 bg-gray-200 rounded-full transition-colors hover:bg-gray-300" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-h-0 md:px-6 relative">
        <AnimatePresence mode="wait" initial={false}>
          {selectedBranch ? (
            <motion.div
              key="service-list"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="flex-1 overflow-y-auto no-scrollbar overscroll-contain"
            >
              <ServiceList selectedBranch={selectedBranch} onBack={handleBackToBranches} />
            </motion.div>
          ) : (
            <motion.div
              key="branch-list"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="flex-1 flex flex-col overflow-hidden"
            >
              <div className="px-4 pb-3 flex-shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-900">{t('selectBranch')}</h3>
                </div>

                {/* <div className="mb-1">
                  <FilterTabs />
                </div> */}
              </div>

              <div className="flex-1 overflow-y-auto no-scrollbar overscroll-contain">
                <BranchList onBranchClick={handleBranchClick} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
